import {
  Button,
  Icon,
  Menu,
  MultiActionButton,
  SimpleDialog,
  Spinner,
  useToastContext,
} from "@ttc3k/trekker";
import { TagManagerFlyout } from "../TagManagerFlyout";
import { useTranslation } from "react-i18next";
import {
  GitMerge,
  NavArrowDown,
  UnionHorizAlt,
  XmarkCircle,
} from "iconoir-react";
import {
  TagDetailsFlyoutMenuAction,
  useTagDetailsFlyout,
  UseTagDetailsFlyoutProps,
} from "./useTagDetailsFlyout";
import { FormProvider } from "react-hook-form";
import { useTagDetailsFlyoutForm } from "./useTagDetailsFlyoutForm";
import { Center } from "styled-system/jsx";
import { SimpleErrorNotification } from "shared/components";
import { TagInformationForm } from "../../components";
import { getMultilingualString } from "shared/utils";
import { TagEntityUsage } from "../../components/TagEntityUsage/TagEntityUsage";
import { TagSplitFlyup } from "./TagSplitFlyup/TagSplitFlyup";
import {
  EnumOwnerType,
  EnumUserSystemPermissions,
  TagByIdQuery,
  TagManyWithCountsQuery,
} from "gql/generated";
import { TagMergeFlyup } from "./TagMergeFlyup/TagMergeFlyup";
import { TagGroupWithTags } from "../../useTagManager";
import { useAppContext } from "useAppContext";
import { useMemo } from "react";
import { useLoggedInAs } from "shared/hooks";

type TagDetailsFlyoutProps = UseTagDetailsFlyoutProps & {
  allTagGroupsWithTags: TagGroupWithTags[];
  onTagUpdated: (tag: TagByIdQuery["tagById"]) => void;
  onTagSplitCompleted: (
    updatedTag: TagByIdQuery["tagById"],
    newTags: TagByIdQuery["tagById"][],
  ) => void;
  onTagMergeCompleted: (
    mergedTag: TagManyWithCountsQuery["tagMany"][0],
  ) => void;
  /** When true, show and enable entity type selection (e.g. in org Custom Tags) */
  enableEntityTypeSelection?: boolean;
};

export const TagDetailsFlyout = ({
  isOpen,
  onClose,
  selectedTagId,
  allTagGroupsWithTags,
  flyoutCategoryOptions,
  onTagUpdated,
  onTagDeleted,
  onTagSplitCompleted,
  onTagMergeCompleted,
  tagMutationCompleteRefreshesQueries,
  enableEntityTypeSelection = false,
}: TagDetailsFlyoutProps) => {
  const { dbUser } = useAppContext();
  const loggedInAs = useLoggedInAs();
  const { t } = useTranslation("adminland", {
    keyPrefix: "PAGE.TAG_MANAGER.FLYOUTS.TAG_DETAILS",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();

  const isAdminland = loggedInAs?.type === EnumOwnerType.TnbInternal;

  const {
    tag,
    loading,
    error,
    deleteTagLoading,
    openModal,
    setOpenModal,
    onDeleteConfirmed,
  } = useTagDetailsFlyout({
    isOpen,
    onClose,
    selectedTagId,
    flyoutCategoryOptions,
    onTagDeleted,
    tagMutationCompleteRefreshesQueries,
  });

  const { form, onSubmit } = useTagDetailsFlyoutForm({
    tag,
    onSuccess: tag => {
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("SUCCESS", {
          label: getMultilingualString(tag?.name ?? {}),
        }),
      });
      onTagUpdated(tag);
      onClose();
      setTimeout(() => form.reset(), 350);
    },
    onError: () => {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("ERRORS.TAG_UPDATE"),
      });
    },
    tagMutationCompleteRefreshesQueries,
  });

  /**
   * Disable the actions if the user does not have the necessary system permissions
   */
  const { canDeleteTag, canMergeTag, canSplitTag, canUpdateTag } =
    useMemo(() => {
      const canOrgUserDelete =
        loggedInAs?.type === EnumOwnerType.Organization &&
        loggedInAs?.entityId === tag?.owner?.entityId;
      return loggedInAs?.type === EnumOwnerType.TnbInternal
        ? {
            canDeleteTag:
              dbUser?.systemPermissions?.includes(
                EnumUserSystemPermissions.TagDelete,
              ) ?? false,
            canMergeTag:
              dbUser?.systemPermissions?.includes(
                EnumUserSystemPermissions.TagMerge,
              ) ?? false,
            canSplitTag:
              dbUser?.systemPermissions?.includes(
                EnumUserSystemPermissions.TagSplit,
              ) ?? false,
            canUpdateTag:
              dbUser?.systemPermissions?.includes(
                EnumUserSystemPermissions.TagUpdate,
              ) ?? false,
          }
        : {
            canDeleteTag: canOrgUserDelete,
            canMergeTag: canOrgUserDelete,
            canSplitTag: canOrgUserDelete,
            canUpdateTag: canOrgUserDelete,
          };
    }, [dbUser, loggedInAs, tag]);

  return (
    <>
      <TagManagerFlyout
        isOpen={isOpen}
        onClose={onClose}
        title={t("TITLE")}
        description={t("DESCRIPTION")}
        error={false}
        loading={false}
        footer={
          <>
            <Menu.Root
              trigger={
                <MultiActionButton mode="light" Icon={NavArrowDown}>
                  {t("CTA_BUTTONS.ACTIONS")}
                </MultiActionButton>
              }
              onSelect={({ value }: { value: string }) =>
                setOpenModal(value as TagDetailsFlyoutMenuAction)
              }
            >
              {/* Merge */}
              <Menu.Item
                value="merge"
                alignItems="center"
                justifyContent="start"
                gap="100"
                color="text.accent.blue.mid"
                disabled={!canMergeTag}
              >
                <Icon
                  Element={UnionHorizAlt}
                  color="icon.accent.blue.mid !important"
                />
                {t("CTA_BUTTONS.MERGE")}
              </Menu.Item>
              {/* Split */}
              <Menu.Item
                value="split"
                alignItems="center"
                justifyContent="start"
                gap="100"
                color="text.accent.blue.mid"
                disabled={!canSplitTag}
              >
                <Icon
                  Element={GitMerge}
                  color="icon.accent.blue.mid !important"
                />
                {t("CTA_BUTTONS.SPLIT")}
              </Menu.Item>
              {/* Delete */}
              <Menu.Item
                value="delete"
                alignItems="center"
                justifyContent="start"
                gap="100"
                color="text.error.mid"
                disabled={deleteTagLoading || !canDeleteTag}
              >
                <Icon Element={XmarkCircle} color="icon.error.mid !important" />
                {t("CTA_BUTTONS.DELETE")}
              </Menu.Item>
            </Menu.Root>
            <Button
              visual="accent.blue"
              disabled={!form.formState.isValid || !canUpdateTag}
              isLoading={form.formState.isSubmitting || deleteTagLoading}
              type="submit"
              onClick={onSubmit}
            >
              {t("CTA_BUTTONS.SAVE")}
            </Button>
          </>
        }
      >
        <FormProvider {...form}>
          <form
            id="tag-details-flyout-form"
            onSubmit={onSubmit}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            {loading ? (
              <Center py="1000">
                <Spinner w={"40px"} h={"40px"} />
              </Center>
            ) : error ? (
              <SimpleErrorNotification />
            ) : (
              <>
                <TagInformationForm
                  flyoutCategoryOptions={flyoutCategoryOptions}
                  isAdminland={isAdminland}
                  enableEntityTypeSelection={enableEntityTypeSelection}
                />
                <TagEntityUsage selectedTagId={selectedTagId} />
              </>
            )}
          </form>
        </FormProvider>
        {/* Split */}
        <TagSplitFlyup
          tag={tag}
          isOpen={openModal === "split"}
          onClose={() => setOpenModal(null)}
          onTagSplitCompleted={(updatedTag, newTags) => {
            onTagSplitCompleted(updatedTag, newTags);
            onClose();
          }}
          refetchQueries={tagMutationCompleteRefreshesQueries}
        />
        {/* Merge */}
        <TagMergeFlyup
          tag={tag}
          allTagGroupsWithTags={allTagGroupsWithTags}
          isOpen={openModal === "merge"}
          onClose={() => setOpenModal(null)}
          onTagMergeCompleted={mergedTag => {
            onTagMergeCompleted(mergedTag);
            onClose();
          }}
          refetchQueries={tagMutationCompleteRefreshesQueries}
        />
      </TagManagerFlyout>
      {/* Delete confirmation dialog */}
      <SimpleDialog
        open={openModal === "delete"}
        title={t("DELETE_MODAL.TITLE")}
        message={t("DELETE_MODAL.DESCRIPTION", {
          label: getMultilingualString(tag?.name ?? {}),
        })}
        Icon={XmarkCircle}
        cancelButtonCopy={t("DELETE_MODAL.CANCEL")}
        onCancelClick={() => setOpenModal(null)}
        confirmButtonCopy={t("DELETE_MODAL.DELETE")}
        confirmButtonVisual="destructive"
        onConfirmClick={onDeleteConfirmed}
        portal
      />
    </>
  );
};
